import { useCallback } from "react";
import { useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import type { FundDetails } from "../../../../api/types/fundStructureTypes";
import useClientContext from "../../../../context/useClientContext";
import FundDetailsPage from "./FundDetailsPage";

const FundStructureFundPage = () => {
  usePageTitle("Fund");

  const { accessCategories } = useClientContext();

  const { id } = useParams();

  const getFundDetails = useCallback(() => adminApi.getFundDetails(defined(id)), [id]);
  const [fundDetails, detailsError, { setData: setFundDetails }] = useFetch(getFundDetails);

  const getObjectDefinition = useCallback(() => adminApi.getObjectDefinition("Fund"), []);
  const [objectDefinitionWithPermissions, fetchDefinitionError] = useFetch(getObjectDefinition);

  const handleFundDetailsChange = useCallback(
    (details: Partial<FundDetails>) => {
      setFundDetails((prev) => (prev ? { ...prev, ...details } : undefined));
    },
    [setFundDetails]
  );

  if (detailsError || fetchDefinitionError) {
    logError(detailsError || fetchDefinitionError, "[FundStructureFundPage]");
    return <DataLoadingFailed title="Loading fund details failed" />;
  }

  if (!fundDetails || !objectDefinitionWithPermissions) {
    return <InlineLoader />;
  }

  return (
    <FundDetailsPage
      fundDetails={fundDetails}
      objectDefinition={objectDefinitionWithPermissions.objectClassDefinition}
      objectPermissions={objectDefinitionWithPermissions.objectPermissions}
      accessCategories={accessCategories}
      onFundDetailsChange={handleFundDetailsChange}
    />
  );
};

export default FundStructureFundPage;
